import { useState, useEffect, useRef } from "react";
import { Sparkles, X } from "lucide-react";

// ─── Utilities ────────────────────────────────────────────────────────────────

const rtl = (n, len) =>
  String(n).padStart(len, "0").split("").reverse().map(Number);

const ltr = (n) => String(n).split("").map(Number);

const COL = ["ones", "tens", "hundreds", "thousands", "ten-thousands"];
const HL = (t) => `<span style="color:#D85A30;font-weight:500">${t}</span>`;

// ─── Step Builders ────────────────────────────────────────────────────────────

function buildAddSteps(op1, op2, ans) {
  const nc = Math.max(String(op1).length, String(op2).length, String(ans).length);
  const a = rtl(op1, nc), b = rtl(op2, nc);
  let carry = 0;
  const steps = [];
  for (let i = 0; i < nc; i++) {
    const sum = a[i] + b[i] + carry;
    steps.push({ i, top: a[i], bottom: b[i], carryIn: carry, digit: sum % 10, carryOut: Math.floor(sum / 10), sum });
    carry = Math.floor(sum / 10);
  }
  return steps;
}

function buildSubSteps(op1, op2) {
  const nc = Math.max(String(op1).length, String(op2).length);
  const a = rtl(op1, nc), b = rtl(op2, nc);
  let borrowed = 0;
  const steps = [];
  for (let i = 0; i < nc; i++) {
    const origTop = a[i];
    const top = origTop - borrowed;
    const needBorrow = top < b[i];
    const effTop = needBorrow ? top + 10 : top;
    steps.push({ i, origTop, top, bottom: b[i], needBorrow, effTop, digit: effTop - b[i] });
    borrowed = needBorrow ? 1 : 0;
  }
  return steps;
}

function buildMulSteps(op1, multiplier, ans) {
  const nc = Math.max(String(op1).length, String(ans).length);
  const digits = rtl(op1, nc);
  let carry = 0;
  const steps = [];
  for (let i = 0; i < nc; i++) {
    const d = digits[i];
    const product = d * multiplier + carry;
    steps.push({ i, topDigit: d, multiplier, carryIn: carry, digit: product % 10, carryOut: Math.floor(product / 10), product });
    carry = Math.floor(product / 10);
  }
  return steps;
}

function buildDivSteps(dividend, divisor) {
  const divDigits = ltr(dividend);
  const steps = [];
  let partial = 0;
  for (let i = 0; i < divDigits.length; i++) {
    partial = partial * 10 + divDigits[i];
    const q = Math.floor(partial / divisor);
    const r = partial % divisor;
    steps.push({ i, divDigit: divDigits[i], partial, q, r, divisor });
    partial = r;
  }
  return steps;
}

// ─── Hints ────────────────────────────────────────────────────────────────────

function hintAdd(step, afterWrong) {
  const cin = step.carryIn > 0 ? ` + carry ${HL(step.carryIn)}` : "";
  if (!afterWrong)
    return `Add the ${HL(COL[step.i] || "column")}: ${step.top} + ${step.bottom}${cin} = ?`;
  if (step.carryOut > 0)
    return `${step.top} + ${step.bottom}${cin} = ${HL(step.sum)} — write ${HL(step.digit)}, carry ${HL(1)}.`;
  return `Try again — ${step.top} + ${step.bottom}${cin} = ?`;
}

function hintAddOk(step) {
  const cn = step.carryOut > 0 ? `, carry ${HL(step.carryOut)}` : "";
  const cin = step.carryIn > 0 ? ` + ${step.carryIn}` : "";
  return `${step.top} + ${step.bottom}${cin} = ${HL(step.digit)}${cn}. Correct!`;
}

function hintSub(step, afterWrong, visTop) {
  const col = COL[step.i] || `column ${step.i + 1}`;
  const t = visTop !== undefined ? visTop : step.origTop;
  if (!afterWrong)
    return `What is ${HL(t)} − ${HL(step.bottom)}? (${col} column)`;
  if (step.needBorrow)
    return `${t} − ${step.bottom} needs borrowing! ${HL(t)} becomes ${HL(step.effTop)}. Now try: ${step.effTop} − ${step.bottom} = ?`;
  return `Try again — ${HL(t)} − ${HL(step.bottom)} = ?`;
}

function hintSubOk(step) {
  return `${HL(step.effTop)} − ${step.bottom} = ${HL(step.digit)}. Correct!`;
}

function hintMul(step, afterWrong) {
  const cin = step.carryIn > 0 ? ` + carry ${HL(step.carryIn)}` : "";
  if (!afterWrong) {
    if (step.carryIn > 0)
      return `Multiply ${HL(step.topDigit)} × ${HL(step.multiplier)}, then add carry ${HL(step.carryIn)} = ?`;
    return `Multiply ${HL(step.topDigit)} × ${HL(step.multiplier)} = ?`;
  }
  if (step.carryOut > 0)
    return `${step.topDigit} × ${step.multiplier}${cin} = ${HL(step.product)} — write ${HL(step.digit)}, carry ${HL(step.carryOut)}.`;
  return `Try again — ${step.topDigit} × ${step.multiplier}${cin} = ?`;
}

function hintMulOk(step) {
  const cn = step.carryOut > 0 ? `, carry ${HL(step.carryOut)}` : "";
  const cin = step.carryIn > 0 ? ` + ${step.carryIn}` : "";
  return `${step.topDigit} × ${step.multiplier}${cin} = ${HL(step.product)} → write ${HL(step.digit)}${cn}. Correct!`;
}

function hintDiv(step, afterWrong) {
  if (step.q === 0 && !afterWrong)
    return `${HL(step.divisor)} doesn't go into ${HL(step.partial)} — write ${HL(0)}.`;
  if (!afterWrong)
    return `How many times does ${HL(step.divisor)} go into ${HL(step.partial)}?`;
  return `Think: ${step.divisor} × ? = ${HL(step.partial)}. Try again.`;
}

function hintDivOk(step) {
  if (step.q === 0)
    return `${step.divisor} × ${HL(0)} = ${HL(step.partial)}. Write 0 and bring down the next digit.`;
  return `${step.divisor} × ${HL(step.q)} = ${HL(step.partial)}. Correct!`;
}

function completionHint(op1, op2, operation, ans) {
  const sym = { "+": "+", "-": "−", "x": "×", "÷": "÷" }[operation] || operation;
  return `${HL(`${op1} ${sym} ${op2} = ${ans}`)}. You solved it step by step!`;
}

function getStepLabel(stepIdx, total, operation, steps) {
  if (stepIdx >= total) return "Problem solved!";
  if (operation === "÷") return `Step ${stepIdx + 1} of ${total} →`;
  const col = COL[steps[stepIdx].i] || `column ${steps[stepIdx].i + 1}`;
  if (stepIdx === 0) return `Start with the ${col} column →`;
  if (stepIdx === total - 1) return `Last — ${col} column →`;
  return `Now the ${col} column →`;
}

// ─── Column Grid ──────────────────────────────────────────────────────────────
// Operator is rendered as a prefix OUTSIDE the grid so all numCols are used for digits.

function ColumnGrid({ steps, numCols, operation, op2, annotations, activeStep, onInput, inputRefs }) {
  const cellW = numCols <= 3 ? 56 : numCols === 4 ? 50 : 42;
  const carryH = 20, rowH = 44;
  const sym = { "+": "+", "-": "−", "x": "×" }[operation];

  // LTR display: dIdx=0 = leftmost = highest place value = RTL colIdx = numCols-1-dIdx
  const cols = Array.from({ length: numCols }, (_, dIdx) => ({
    colIdx: numCols - 1 - dIdx,
    dIdx,
    step: steps[numCols - 1 - dIdx],
    ann: annotations[numCols - 1 - dIdx] || {},
  }));

  // op2 digits LTR, padded to numCols with leading zeros
  const op2LTR = String(op2).padStart(numCols, "0").split("").map(Number);
  const firstNonZeroOp2 = op2LTR.findIndex((x) => x !== 0);

  // Helper: get digit to show in row 2 (op2 row) for a given dIdx
  const getOp2Display = (dIdx) => {
    if (operation === "x") {
      // Show multiplier only in ones column (rightmost), blank elsewhere
      return dIdx === numCols - 1 ? String(op2) : "";
    }
    const d = op2LTR[dIdx];
    // Hide leading zeros (show blank)
    const isLeadingZero = d === 0 && (firstNonZeroOp2 === -1 ? dIdx < numCols - 1 : dIdx < firstNonZeroOp2);
    return isLeadingZero ? "" : String(d);
  };

  // Helper: get digit to show in row 1 (op1 row) for a given colIdx
  const getOp1Display = (colIdx, step) => {
    if (operation === "x") return step ? String(step.topDigit) : "";
    const d = step ? (step.origTop !== undefined ? step.origTop : step.top) : 0;
    return String(d);
  };

  // Compute total grid width
  const totalW = numCols * cellW;

  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", marginBottom: "1.5rem" }}>
      {/* Operator prefix column */}
      <div style={{ width: 28, flexShrink: 0 }}>
        {/* Carry row spacer */}
        <div style={{ height: carryH }} />
        {/* op1 row spacer */}
        <div style={{ height: rowH }} />
        {/* op2 row — operator symbol */}
        <div style={{ height: carryH }} />
        <div style={{ height: rowH, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 22, fontFamily: "monospace", color: "#64748B" }}>{sym}</span>
        </div>
        {/* Divider spacer */}
        <div style={{ height: 9 }} />
        {/* Answer row spacer */}
        <div style={{ height: carryH }} />
        <div style={{ height: rowH }} />
      </div>

      {/* Digit grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `${cellW}px `.repeat(numCols).trim(),
          gap: 0,
          width: totalW,
        }}
      >
        {/* ── Row 1: carry values + op1 digits ── */}
        {cols.map(({ colIdx, step, ann }) => (
          <div key={`r1-${colIdx}`} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            {/* Carry / borrow annotation */}
            <div style={{ height: carryH, fontSize: 12, fontFamily: "monospace", fontWeight: 600, color: "#D85A30", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {ann.carryVal || ""}
            </div>
            {/* Digit */}
            <div style={{ height: rowH, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span style={{ fontSize: 22, fontFamily: "monospace", fontWeight: 500, color: ann.struck ? "#B4B2A9" : "#1E293B", position: "relative", display: "inline-block" }}>
                {getOp1Display(colIdx, step)}
                {ann.struck && (
                  <span style={{ position: "absolute", left: -3, right: -3, top: "75%", height: 2, background: "#888780", transform: "rotate(-18deg)", transformOrigin: "left center", borderRadius: 1, display: "block" }} />
                )}
              </span>
            </div>
          </div>
        ))}

        {/* ── Row 2: op2 digits ── */}
        {cols.map(({ colIdx, dIdx }) => {
          const display = getOp2Display(dIdx);
          return (
            <div key={`r2-${colIdx}`} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ height: carryH }} />
              <div style={{ height: rowH, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ fontSize: 22, fontFamily: "monospace", fontWeight: 500, color: "#1E293B" }}>
                  {display}
                </span>
              </div>
            </div>
          );
        })}

        {/* ── Divider ── */}
        <div style={{ gridColumn: "1 / -1", height: 1.5, background: "#CBD5E1", margin: "4px 0" }} />

        {/* ── Row 3: answer inputs ── */}
        {cols.map(({ colIdx }) => {
          const ann = annotations[colIdx] || {};
          const isActive = colIdx === activeStep;
          return (
            <div key={`r3-${colIdx}`} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ height: carryH }} />
              <div style={{ height: rowH, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <input
                  ref={(el) => { inputRefs.current[colIdx] = el; }}
                  type="text" inputMode="numeric" maxLength={1} placeholder="?"
                  disabled={ann.locked || !isActive}
                  defaultValue=""
                  style={{
                    width: Math.min(cellW - 14, 40), height: 40,
                    border: `2px solid ${ann.locked ? "#0F6E56" : isActive ? "#534AB7" : "#CBD5E1"}`,
                    borderRadius: 8, textAlign: "center",
                    fontSize: 20, fontFamily: "monospace", fontWeight: 500,
                    background: ann.locked ? "#E1F5EE" : "#fff",
                    color: ann.locked ? "#085041" : "#1E293B",
                    outline: "none",
                    boxShadow: isActive && !ann.locked ? "0 0 0 3px #EEEDFE" : "none",
                    boxSizing: "border-box",
                    cursor: ann.locked || !isActive ? "not-allowed" : "text",
                  }}
                  onChange={(e) => {
                    if (e.target.disabled) return;
                    const v = e.target.value.trim();
                    e.target.value = "";
                    if (v) onInput(v, colIdx);
                  }}
                  data-testid={`swm-input-${colIdx}`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Division Grid ────────────────────────────────────────────────────────────

function DivisionGrid({ steps, divisor, dividend, annotations, activeStep, onInput, inputRefs }) {
  const numCols = steps.length;
  const cellW = numCols <= 4 ? 50 : 40;
  const divDigits = ltr(dividend);

  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
        {/* Divisor prefix */}
        <div style={{ paddingRight: 4, paddingBottom: 2 }}>
          <span style={{ fontSize: 22, fontFamily: "monospace", fontWeight: 500, color: "#64748B" }}>{divisor} )</span>
        </div>
        {/* Two-row grid: quotient inputs + dividend digits */}
        <div>
          {/* Quotient inputs */}
          <div style={{ display: "grid", gridTemplateColumns: `${cellW}px `.repeat(numCols).trim() }}>
            {divDigits.map((_, i) => {
              const ann = annotations[i] || {};
              const isActive = i === activeStep;
              return (
                <div key={`q${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 44 }}>
                  <input
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text" inputMode="numeric" maxLength={1} placeholder="?"
                    disabled={ann.locked || !isActive}
                    defaultValue=""
                    style={{
                      width: Math.min(cellW - 12, 36), height: 36,
                      border: `2px solid ${ann.locked ? "#0F6E56" : isActive ? "#534AB7" : "#CBD5E1"}`,
                      borderRadius: 8, textAlign: "center",
                      fontSize: 18, fontFamily: "monospace", fontWeight: 500,
                      background: ann.locked ? "#E1F5EE" : "#fff",
                      color: ann.locked ? "#085041" : "#1E293B",
                      outline: "none",
                      boxShadow: isActive && !ann.locked ? "0 0 0 3px #EEEDFE" : "none",
                      boxSizing: "border-box",
                    }}
                    onChange={(e) => {
                      if (e.target.disabled) return;
                      const v = e.target.value.trim();
                      e.target.value = "";
                      if (v) onInput(v, i);
                    }}
                    data-testid={`swm-div-input-${i}`}
                  />
                </div>
              );
            })}
          </div>
          {/* Dividend digits */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `${cellW}px `.repeat(numCols).trim(),
              borderTop: "1.5px solid #CBD5E1",
            }}
          >
            {divDigits.map((d, i) => (
              <div key={`d${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 44 }}>
                <span style={{ fontSize: 22, fontFamily: "monospace", fontWeight: 500, color: "#1E293B" }}>{d}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SolveWithMitraa({ operand1, operand2, operation, answer, onClose }) {
  const op1 = parseInt(operand1);
  const op2 = parseInt(operand2);
  const ans = parseInt(answer);

  const steps = (() => {
    if (operation === "+") return buildAddSteps(op1, op2, ans);
    if (operation === "-") return buildSubSteps(op1, op2);
    if (operation === "x") return buildMulSteps(op1, op2, ans);
    if (operation === "÷") return buildDivSteps(op1, op2);
    return [];
  })();
  const numCols = steps.length;

  const getInitialHint = () => {
    if (!steps.length) return "";
    const s = steps[0];
    if (operation === "+") return hintAdd(s, false);
    if (operation === "-") return hintSub(s, false, s.origTop);
    if (operation === "x") return hintMul(s, false);
    if (operation === "÷") return hintDiv(s, false);
    return "";
  };

  const [activeStep, setActiveStep] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [hint, setHint] = useState(getInitialHint);
  const [label, setLabel] = useState(() => getStepLabel(0, numCols, operation, steps));
  const [annotations, setAnnotations] = useState({});
  const [wrongAttempts, setWrongAttempts] = useState({});
  const inputRefs = useRef({});

  useEffect(() => {
    const t = setTimeout(() => inputRefs.current[0]?.focus(), 150);
    return () => clearTimeout(t);
  }, []);

  const patchAnn = (colIdx, patch) =>
    setAnnotations((prev) => ({ ...prev, [colIdx]: { ...prev[colIdx], ...patch } }));

  const shakeInput = (colIdx) => {
    const el = inputRefs.current[colIdx];
    if (!el) return;
    el.style.animation = "none";
    void el.offsetWidth;
    el.style.border = "2px solid #993C1D";
    el.style.background = "#FAECE7";
    el.style.animation = "swm-shake 0.35s ease";
    setTimeout(() => {
      if (el.disabled) return; // might have been locked since
      el.style.animation = "";
      el.style.border = "2px solid #534AB7";
      el.style.background = "#fff";
      el.focus();
    }, 380);
  };

  const lockInput = (colIdx, val) => {
    const el = inputRefs.current[colIdx];
    if (el) {
      el.value = val;
      el.disabled = true;
      el.style.border = "2px solid #0F6E56";
      el.style.background = "#E1F5EE";
      el.style.color = "#085041";
      el.style.boxShadow = "";
    }
    patchAnn(colIdx, { locked: true });
  };

  const focusStep = (colIdx) => {
    const el = inputRefs.current[colIdx];
    if (el) {
      el.disabled = false;
      el.style.border = "2px solid #534AB7";
      el.style.background = "#fff";
      el.style.color = "#1E293B";
      el.style.boxShadow = "0 0 0 3px #EEEDFE";
      setTimeout(() => el.focus(), 60);
    }
  };

  const handleInput = (val, colIdx) => {
    if (!val) return;
    const step = steps[colIdx];
    const expected = String(step.digit !== undefined ? step.digit : step.q);
    const wrong = wrongAttempts[colIdx] || 0;

    if (val === expected) {
      // ─── CORRECT ───────────────────────────────────────────
      lockInput(colIdx, val);
      const nextIdx = colIdx + 1;

      if (operation === "+" && step.carryOut > 0 && nextIdx < numCols)
        patchAnn(nextIdx, { carryVal: "1" });
      if (operation === "x" && step.carryOut > 0 && nextIdx < numCols)
        patchAnn(nextIdx, { carryVal: String(step.carryOut) });

      if (operation === "+") setHint(hintAddOk(step));
      else if (operation === "-") setHint(hintSubOk(step));
      else if (operation === "x") setHint(hintMulOk(step));
      else if (operation === "÷") setHint(hintDivOk(step));

      setTimeout(() => {
        if (nextIdx >= numCols) {
          setIsDone(true);
          setLabel("Problem solved!");
          setHint(completionHint(op1, op2, operation, ans));
        } else {
          setActiveStep(nextIdx);
          setLabel(getStepLabel(nextIdx, numCols, operation, steps));
          focusStep(nextIdx);
          setTimeout(() => {
            const ns = steps[nextIdx];
            if (operation === "+") setHint(hintAdd(ns, false));
            else if (operation === "-") {
              setAnnotations((prev) => {
                const vt = prev[nextIdx]?.carryVal
                  ? parseInt(prev[nextIdx].carryVal)
                  : ns.origTop;
                setHint(hintSub(ns, false, vt));
                return prev;
              });
            }
            else if (operation === "x") setHint(hintMul(ns, false));
            else if (operation === "÷") setHint(hintDiv(ns, false));
          }, 400);
        }
      }, 500);
    } else {
      // ─── WRONG ─────────────────────────────────────────────
      setWrongAttempts((prev) => ({ ...prev, [colIdx]: wrong + 1 }));
      shakeInput(colIdx);

      if (wrong === 0) {
        if (operation === "+" && step.carryOut > 0) {
          setHint(hintAdd(step, true));
          if (colIdx + 1 < numCols) patchAnn(colIdx + 1, { carryVal: "1" });
        } else if (operation === "-" && step.needBorrow) {
          setAnnotations((prev) => {
            const vt = prev[colIdx]?.carryVal ? parseInt(prev[colIdx].carryVal) : step.origTop;
            setHint(hintSub(step, true, vt));
            return prev;
          });
          setTimeout(() => {
            patchAnn(colIdx, { struck: true, carryVal: String(step.effTop) });
            if (colIdx + 1 < numCols) {
              const donor = steps[colIdx + 1];
              if (donor.origTop >= 1) {
                patchAnn(colIdx + 1, { struck: true, carryVal: String(donor.origTop - 1) });
              }
            }
          }, 350);
        } else if (operation === "x" && step.carryOut > 0) {
          setHint(hintMul(step, true));
          if (colIdx + 1 < numCols) patchAnn(colIdx + 1, { carryVal: String(step.carryOut) });
        } else if (operation === "÷") {
          setHint(hintDiv(step, true));
        } else {
          if (operation === "+") setHint(hintAdd(step, true));
          else if (operation === "-") {
            setAnnotations((prev) => {
              const vt = prev[colIdx]?.carryVal ? parseInt(prev[colIdx].carryVal) : step.origTop;
              setHint(hintSub(step, false, vt));
              return prev;
            });
          }
        }
      } else {
        if (operation === "+")
          setHint(`Try again — ${step.top} + ${step.bottom}${step.carryIn > 0 ? ` + ${step.carryIn}` : ""} = ?`);
        else if (operation === "-") {
          setAnnotations((prev) => {
            const vt = prev[colIdx]?.carryVal ? parseInt(prev[colIdx].carryVal) : step.effTop;
            setHint(`Try again — ${HL(vt)} − ${HL(step.bottom)} = ?`);
            return prev;
          });
        }
        else if (operation === "x")
          setHint(`Try again — ${step.topDigit} × ${step.multiplier}${step.carryIn > 0 ? ` + ${step.carryIn}` : ""} = ?`);
        else if (operation === "÷")
          setHint(`${step.divisor} × ? = ${HL(step.partial)}. Try once more.`);
      }
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setIsDone(false);
    setAnnotations({});
    setWrongAttempts({});
    setHint(getInitialHint());
    setLabel(getStepLabel(0, numCols, operation, steps));
    Object.values(inputRefs.current).forEach((el) => {
      if (el) {
        el.value = "";
        el.disabled = true;
        el.style.border = "2px solid #CBD5E1";
        el.style.background = "#fff";
        el.style.color = "#1E293B";
        el.style.boxShadow = "";
        el.style.animation = "";
      }
    });
    setTimeout(() => focusStep(0), 100);
  };

  return (
    <>
      <style>{`@keyframes swm-shake{0%,100%{transform:translateX(0)}25%{transform:translateX(-5px)}75%{transform:translateX(5px)}}`}</style>
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
        style={{ background: "rgba(15,23,42,0.65)", backdropFilter: "blur(2px)" }}
        data-testid="swm-overlay"
      >
        <div
          className="w-full bg-white rounded-3xl shadow-2xl overflow-hidden"
          style={{ maxWidth: 460, maxHeight: "90vh", overflowY: "auto" }}
          data-testid="swm-card"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <div className="flex items-center gap-2.5">
              <span
                style={{ background: "#EEEDFE", color: "#3C3489", fontSize: 12, fontWeight: 500, padding: "4px 12px", borderRadius: 20 }}
                data-testid="swm-badge"
              >
                Solve with Mitraa
              </span>
              <span className="text-sm font-medium text-slate-700">Let's work it out together</span>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              data-testid="swm-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Notebook card */}
          <div className="px-5 pb-5">
            <div className="rounded-2xl" style={{ border: "0.5px solid #E2E8F0", padding: "1.25rem" }}>
              {/* Step label */}
              <p
                className="text-center mb-3"
                style={{ fontSize: 12, color: "#64748B", letterSpacing: "0.04em" }}
                data-testid="swm-step-label"
              >
                {label}
              </p>

              {/* Grid */}
              {operation === "÷" ? (
                <DivisionGrid
                  steps={steps}
                  divisor={op2}
                  dividend={op1}
                  annotations={annotations}
                  activeStep={activeStep}
                  onInput={handleInput}
                  inputRefs={inputRefs}
                />
              ) : (
                <ColumnGrid
                  steps={steps}
                  numCols={numCols}
                  operation={operation}
                  op2={op2}
                  annotations={annotations}
                  activeStep={activeStep}
                  onInput={handleInput}
                  inputRefs={inputRefs}
                />
              )}

              {/* Hint */}
              <div
                className="flex gap-2.5 items-start rounded-xl p-3"
                style={{ background: "#EEEDFE" }}
                data-testid="swm-hint"
              >
                <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#534AB7" }} />
                <div
                  className="text-sm leading-relaxed"
                  style={{ color: "#3C3489" }}
                  dangerouslySetInnerHTML={{ __html: hint }}
                />
              </div>

              {/* Completion */}
              {isDone && (
                <div className="flex items-center justify-center gap-3 mt-4" data-testid="swm-done-actions">
                  <button
                    onClick={onClose}
                    style={{ background: "#534AB7", color: "#EEEDFE", border: "none", borderRadius: 20, padding: "10px 28px", fontSize: 14, fontWeight: 500, cursor: "pointer" }}
                    data-testid="swm-done-btn"
                  >
                    Done
                  </button>
                  <button
                    onClick={handleReset}
                    style={{ background: "transparent", border: "0.5px solid #CBD5E1", borderRadius: 20, padding: "10px 20px", fontSize: 14, color: "#64748B", cursor: "pointer", fontWeight: 500 }}
                    data-testid="swm-try-again-btn"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
